import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { ArrowLeft, WarningCircle, CheckCircle, Briefcase, Timer } from "@phosphor-icons/react";
import { format } from "date-fns";

function Row({ label, value, strong }) {
  return (
    <div className="flex justify-between py-2 border-b border-border last:border-0">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className={strong ? "font-bold" : "font-medium"}>{value}</span>
    </div>
  );
}

export default function PaychequeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const currency = user?.currency || "CAD";

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ["paycheque", id],
    queryFn: async () => (await api.get(`/paycheques/${id}`)).data,
  });

  if (isLoading) return <div className="text-muted-foreground animate-pulse" data-testid="paycheque-detail-loading">Loading…</div>;
  if (isError || !c) return <div className="text-muted-foreground" data-testid="paycheque-detail-error">Paycheque not found.</div>;

  const under = c.status === "Underpaid";

  return (
    <div className="animate-fade-up max-w-2xl" data-testid="paycheque-detail-page">
      <Link to="/paycheques" data-testid="back-to-paycheques" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" weight="bold" /> Back to Paycheques
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className={`h-11 w-11 rounded-md flex items-center justify-center ${
          under ? "bg-destructive/20 text-destructive" : "bg-emerald-500/20 text-emerald-400"}`}>
          {under ? <WarningCircle className="h-7 w-7" weight="fill" /> : <CheckCircle className="h-7 w-7" weight="fill" />}
        </div>
        <div>
          <p className="overline text-muted-foreground">Paycheque</p>
          <h1 className="text-2xl font-black tracking-tight">
            {format(new Date(c.period_start + "T00:00:00"), "MMM d")} – {format(new Date(c.period_end + "T00:00:00"), "MMM d, yyyy")}
          </h1>
        </div>
      </div>

      {under && (
        <Card className="p-5 mb-6 bg-destructive/10 border-destructive/40" data-testid="detail-underpaid-banner">
          <div className="flex items-center gap-2 mb-1">
            <WarningCircle className="h-5 w-5 text-destructive" weight="fill" />
            <span className="font-bold text-destructive">Underpaid by {money(c.shortfall, currency)}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Gross pay received is more than 1% below your expected earnings for this period.
          </p>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 bg-card border-border">
          <p className="overline text-muted-foreground mb-3">Received</p>
          <Row label="Gross pay" value={money(c.gross_pay, currency)} strong />
          <Row label="Net pay" value={money(c.net_pay, currency)} />
          <Row label="Paid duty hours" value={`${c.paid_duty_hours} h`} />
          <Row label="Overtime hours" value={`${c.overtime_hours} h`} />
        </Card>
        <Card className="p-5 bg-card border-border">
          <p className="overline text-muted-foreground mb-3">Expected</p>
          <Row label={c.base_source === "standard_gross" ? "Standard gross" : "Regular"}
            value={money(c.expected_base ?? c.expected_regular, currency)} />
          <Row label="Overtime" value={money(c.expected_overtime, currency)} />
          <Row label="Paid duty" value={money(c.expected_paid_duty, currency)} />
          <Row label="Expected gross" value={money(c.expected_gross, currency)} strong />
        </Card>
      </div>

      <Card className="p-5 mt-4 bg-card border-border">
        <div className="flex justify-between items-center">
          <span className="font-semibold">Shortfall</span>
          <span className={`text-xl font-black ${c.shortfall > 0 ? "text-destructive" : "text-emerald-400"}`} data-testid="detail-shortfall">
            {money(c.shortfall, currency)}
          </span>
        </div>
      </Card>

      {c.unpaid_entries?.length > 0 && (
        <div className="mt-6" data-testid="unpaid-entries-section">
          <p className="overline text-destructive mb-3">Expected but not paid</p>
          <Card className="bg-card border-border overflow-hidden">
            {c.unpaid_entries.map((e, i) => (
              <div key={i} data-testid={`unpaid-entry-${i}`}
                className="flex items-center justify-between px-5 py-4 border-b border-border last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center ${
                    e.type === "overtime" ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                    {e.type === "overtime" ? <Timer className="h-4 w-4" weight="bold" /> : <Briefcase className="h-4 w-4" weight="bold" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{e.type === "overtime" ? "Overtime" : "Paid Duty"} · {e.detail}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(e.entry_date + "T00:00:00"), "MMM d, yyyy")} · {e.hours} h
                    </p>
                  </div>
                </div>
                <span className="font-bold text-destructive">{money(e.amount, currency)}</span>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
